import React, { useCallback, memo} from 'react';

import ArrayEditor from './ArrayEditor';
import useIsMobile from './useIsMobile';
import ProjectSelect from './ProjectSelect';
import ArticleSelect, { isValidArticle } from './ArticleSelect';
import CurrencyInput, { isValidCurrency } from './CurrencyInput';
import TextInput, { isValidText } from './TextInput';
import QuantityInput, { isValidQuantity } from './QuantityInput';
import CostCenterItemSelect from './CostCenterItemSelect';
import Alert from '@mui/material/Alert';
import { useScopeKeys, scopedTree, noIncomePairReason } from './Scope';
import { CostCenterItem, CustomerInvoiceRow, ProjectWithArticles, Article, Account } from './Types'


export function isValidCustomerInvoiceRows(rows: CustomerInvoiceRow[]) {
    function isValidRow(row: CustomerInvoiceRow) {
        return isValidArticle(row.articleId) && isValidText(row.text) && isValidQuantity(row.quantity) && isValidCurrency(row.unitPrice)
    }
    return rows.length > 0 && rows.every(isValidRow)
}

function getCostCenterItem(costCenterItems: CostCenterItem<ProjectWithArticles>[], costCenterItemId: string | null) {
    return costCenterItems.find(item => item.cost_center_item_id === costCenterItemId) ?? null
}

function getProject(costCenterItems: CostCenterItem<ProjectWithArticles>[], costCenterItemId: string | null, projectId: string | null) {
    const costCenterItem = getCostCenterItem(costCenterItems, costCenterItemId)
    if (costCenterItem === null) {
        return null
    }
    return costCenterItem.projects.find(project => project.project_id === projectId) ?? null
}

function getArticle(costCenterItems: CostCenterItem<ProjectWithArticles>[], costCenterItemId: string | null, projectId: string | null, articleId: string): Article | null {
    const project = getProject(costCenterItems, costCenterItemId, projectId)
    if (project === null) return null 
    return project.articles.find(article => article.article_id === articleId) ?? null
}


const CustomerInvoiceRow = memo(({
        labelsEnabled,
        costCenterItems,
        officialIncomeAccounts,
        rowId,
        row,
        onChangeRow,
        onChangeCostCenterItems,
    }: {
        labelsEnabled: boolean,
        costCenterItems: CostCenterItem<ProjectWithArticles>[],
        officialIncomeAccounts: Account[],
        rowId: number,
        row: CustomerInvoiceRow,
        onChangeRow: (rowId: number, rowMutator: (row: CustomerInvoiceRow) => void) => void,
        onChangeCostCenterItems: (newCostCenterItems: CostCenterItem<ProjectWithArticles>[]) => void,
    }) => {

    const changeTextHandler = useCallback(
        (value: string) => onChangeRow(rowId, row => {row.text = value}),
        [onChangeRow, rowId]
    )
    const changeQuantityHandler = useCallback(
        (value: number | null) => onChangeRow(rowId, row => {row.quantity = value}),
        [onChangeRow, rowId]
    )
    const changeUnitPriceHandler = useCallback(
        (value: number | null) => onChangeRow(rowId, row => {row.unitPrice = value}),
        [onChangeRow, rowId]
    )

    const changeCostCenterItemHandler = useCallback( 
        (newCostCenterItemId: string) => {
            onChangeRow(rowId, row => {
                row.costCenterItemId = newCostCenterItemId
                row.projectId = null
                row.articleId = null
                row.text = ''
                row.quantity = null
                row.unitPrice = null
            })
        },
        [onChangeRow, rowId]
    )

    const changeProjectHandler = useCallback( 
        (newProjectId: string) => {
            onChangeRow(rowId, row => {
                row.projectId = newProjectId
                row.articleId = null
                row.text = ''
                row.quantity = null
                row.unitPrice = null
            })
        },
        [onChangeRow, rowId]
    )

    const changeArticleHandler = useCallback( 
        (articleId: string, newCostCenterItems?: CostCenterItem<ProjectWithArticles>[]) => {
            if (newCostCenterItems === undefined) {
                newCostCenterItems = costCenterItems
            } else {
                onChangeCostCenterItems(newCostCenterItems)
            }
            onChangeRow(rowId, row => {
                const article = getArticle(newCostCenterItems, row.costCenterItemId, row.projectId, articleId)
                row.articleId = articleId
                row.text = article !== null ? article.Name : ''
                row.quantity = article !== null ? 1 : null
                row.unitPrice = article !== null ? article.NetPrice : null
            })
        },
        [onChangeRow, rowId, onChangeCostCenterItems, costCenterItems]
    )

    // Limit picking to the role's scope, same as the expense LocationSelect; the current pair is
    // kept even if out of scope. Value resolution (article options below) still uses the full tree.
    const scopeKeys = useScopeKeys()
    const scopedItems = scopedTree(costCenterItems, scopeKeys, row.costCenterItemId, row.projectId)
    const scopedCostCenterItem = getCostCenterItem(scopedItems, row.costCenterItemId)
    return (
        <>
            <CostCenterItemSelect
                required={true}
                label={labelsEnabled ? 'Kostnadsställe': undefined}
                costCenterItems={scopedItems}
                value={row.costCenterItemId}
                onChange={changeCostCenterItemHandler}
            />
            <ProjectSelect
                disabled={scopedCostCenterItem === null}
                required={true}
                key={row.costCenterItemId}
                label={labelsEnabled ? 'Projekt': undefined}
                costCenterItem={scopedCostCenterItem}
                value={row.projectId}
                onChange={changeProjectHandler}
            />
            <ArticleSelect
                disabled={!row.projectId}
                required={true}
                label={labelsEnabled ? 'Artikel' : undefined}
                project={getProject(costCenterItems, row.costCenterItemId, row.projectId)}
                officialIncomeAccounts={officialIncomeAccounts}
                value={row.articleId}
                onChange={changeArticleHandler}
            />
            <TextInput
                disabled={!row.articleId}
                label={labelsEnabled ? 'Text' : undefined}
                value={row.text}
                onChange={changeTextHandler}
            />
            <QuantityInput
                disabled={!row.articleId}
                label={labelsEnabled ? 'Antal' : undefined}
                value={row.quantity}
                onChange={changeQuantityHandler}
            />
            <CurrencyInput
                disabled={!row.articleId}
                label={labelsEnabled ? 'Pris per styck' : undefined}
                value={row.unitPrice}
                onChange={changeUnitPriceHandler}
            />
        </>
    )
})

export const customerInvoiceRowConstructor: () => CustomerInvoiceRow =
    () => {
        return {
            costCenterItemId: null,
            projectId: null,
            articleId: null,
            text: '',
            quantity: null,
            unitPrice: null
        }
    }


function CustomerInvoiceRows({costCenterItems, rows, onChangeRows, onChangeCostCenterItems, officialIncomeAccounts}: {
    costCenterItems: CostCenterItem<ProjectWithArticles>[],
    rows: CustomerInvoiceRow[],
    onChangeRows: (rowsModifier: (rows: CustomerInvoiceRow[]) => void) => void,
    onChangeCostCenterItems: (newCostCenterItems: CostCenterItem<ProjectWithArticles>[]) => void,
    officialIncomeAccounts: Account[],
}) {
    // On desktop the first row's labels serve as column headers; when rows are stacked (mobile)
    // every row needs its own labels or the later rows are unlabelled selects.
    const isMobile = useIsMobile()
    const noPairReason = noIncomePairReason(costCenterItems, useScopeKeys())

    const RowComponent = useCallback(({rowId, rowIndex, row, onChangeRow}: {
        rowId: number,
        rowIndex: number,
        row: CustomerInvoiceRow,
        onChangeRow: (rowId: number, rowMutator: (row: CustomerInvoiceRow) => void) => void
    }) =>
        <CustomerInvoiceRow
            costCenterItems={costCenterItems}
            officialIncomeAccounts={officialIncomeAccounts}
            onChangeCostCenterItems={onChangeCostCenterItems}
            labelsEnabled={rowIndex === 0 || isMobile}
            rowId={rowId}
            row={row}
            onChangeRow={onChangeRow}
        />,
        [costCenterItems, onChangeCostCenterItems, officialIncomeAccounts, isMobile]
    )

    return <>
        {noPairReason !== null &&
            <Alert severity="info" sx={{ mb: 1 }}>{noPairReason}</Alert>}
        <ArrayEditor
            gridTemplateColumns='repeat(3, min-content) minmax(5rem, 30rem) 8rem 8rem'
            rowGap='0.5rem'
            columnGap='1.5rem'
            rows={rows}
            onChangeRows={onChangeRows}
            rowConstructor={customerInvoiceRowConstructor}
            RowComponent={RowComponent}
        />
    </>
}

export default CustomerInvoiceRows
